import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  afterRenderEffect,
  computed,
  inject,
  signal,
  untracked,
  viewChild
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { HELP_TOPICS } from "../help-topics.config";
import { HelpService } from "../help.service";
import { HelpBlock, HelpGuide, HelpTextSegment, HelpTopic } from "../help.types";
import { helpBlockResource, helpResourceUrl } from "../help-resources.util";
import { matchHelpSections, parseHelpText, visibleHelpTopics } from "../help.util";
import { normalizeChatbotText } from "../../chatbot/chatbot-normalize.util";
import { HelpVideoComponent } from "../help-video/help-video.component";

type ViewBlock =
  | { kind: "text" | "tip" | "warning"; segments: HelpTextSegment[] }
  | { kind: "steps" | "list"; items: HelpTextSegment[][] }
  | { kind: "example"; title: string; items: HelpTextSegment[][] }
  | { kind: "faq"; items: { q: string; a: HelpTextSegment[] }[] }
  | { kind: "image"; url: string; alt: string; caption: string | null }
  | { kind: "video"; mediaId: string; title: string; caption: string | undefined; localSrc: string };

interface ViewSection {
  id: string;
  title: string;
  blocks: ViewBlock[];
}

function toViewBlock(block: HelpBlock, topic: HelpTopic, sectionId: string, guide: HelpGuide): ViewBlock {
  const resource = helpBlockResource(topic, sectionId, block, guide.lang);
  const resourceUrl = resource ? helpResourceUrl(resource) : "";
  switch (block.kind) {
    case "text":
    case "tip":
    case "warning":
      return { kind: block.kind, segments: parseHelpText(block.text) };
    case "steps":
    case "list":
      return { kind: block.kind, items: block.items.map(parseHelpText) };
    case "example":
      return { kind: "example", title: block.title, items: block.items.map(parseHelpText) };
    case "faq":
      return { kind: "faq", items: block.items.map(i => ({ q: i.q, a: parseHelpText(i.a) })) };
    case "image":
      return {
        kind: "image",
        url: resourceUrl,
        alt: block.alt,
        caption: block.caption ?? null
      };
    case "video":
      return {
        kind: "video",
        mediaId: block.mediaId,
        title: block.title,
        caption: block.caption,
        localSrc: resourceUrl
      };
  }
}

function toViewSections(guide: HelpGuide, topic: HelpTopic): ViewSection[] {
  return guide.sections.map(s => ({
    id: s.id,
    title: s.title,
    blocks: s.blocks.map(b => toViewBlock(b, topic, s.id, guide))
  }));
}

/** Contextual screen guides, bounded by desktop navigation chrome and full-screen on mobile. */
@Component({
  selector: "app-help-panel",
  templateUrl: "./help-panel.component.html",
  styleUrl: "./help-panel.component.scss",
  imports: [TranslateModule, CdkTrapFocus, MatButtonModule, MatTooltipModule, HelpVideoComponent],
  host: {
    "(document:keydown.escape)": "onEscape()",
    "(window:resize)": "updateChromeBounds()",
    "[style.--help-panel-top]": "chromeTop()",
    "[style.--help-panel-bottom]": "chromeBottom()"
  }
})
export class HelpPanelComponent {
  protected readonly help = inject(HelpService);
  private readonly SessionDataService = inject(SessionDataService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly chromeTop = signal("0px");
  protected readonly chromeBottom = signal("0px");
  protected readonly isMobile = signal(false);

  private readonly body = viewChild<ElementRef<HTMLElement>>("body");

  protected readonly guide = computed(() => this.help.guide.value() ?? null);
  protected readonly sections = computed(() => {
    const guide = this.guide();
    const topic = this.help.topic();
    return guide && topic ? toViewSections(guide, topic) : [];
  });
  protected readonly summary = computed(() => parseHelpText(this.guide()?.summary ?? ""));
  protected readonly lang = this.help.lang;
  protected readonly media = computed(() => this.help.media.value() ?? []);
  /** Other guides the profile can open when the current screen has none. */
  protected readonly availableTopics = computed(() =>
    visibleHelpTopics(this.SessionDataService.profile(), HELP_TOPICS)
  );
  protected readonly zoomedImage = signal<{ url: string; alt: string } | null>(null);
  protected readonly query = signal("");
  /** Library results: topics matching by name, or with guide sections matching the query. */
  protected readonly libraryResults = computed(() => {
    const entries = this.help.libraryGuides.value() ?? [];
    const q = normalizeChatbotText(this.query());
    return entries
      .map(({ topic, guide }) => {
        const title = guide?.title ?? "";
        const titleMatch =
          q.length > 0 && normalizeChatbotText(`${title} ${guide?.summary ?? ""}`).includes(q);
        const sections = guide && q ? matchHelpSections(guide, this.query(), 3).map(m => m.section) : [];
        return { topic, title, summary: guide?.summary ?? "", titleMatch, sections };
      })
      .filter(r => !q || r.titleMatch || r.sections.length > 0);
  });
  /** Images not captured yet (404) render as a placeholder instead of a broken icon. */
  protected readonly missingImages = signal<ReadonlySet<string>>(new Set());

  protected markImageMissing(url: string): void {
    this.missingImages.update(set => new Set(set).add(url));
  }

  constructor() {
    afterNextRender(() => {
      const observer = new ResizeObserver(() => this.updateChromeBounds());
      for (const selector of [".header-container", ".footer-container"]) {
        const element = document.querySelector(selector);
        if (element) {
          observer.observe(element);
        }
      }
      this.updateChromeBounds();
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
    afterRenderEffect(() => {
      const target = this.help.targetSectionId();
      const container = this.body()?.nativeElement;
      if (!target || !container || this.sections().length === 0) {
        return;
      }
      untracked(() => this.scrollToSection(target));
    });
  }

  protected updateChromeBounds(): void {
    const header = document.querySelector(".header-container");
    const footer = document.querySelector(".footer-container");
    this.chromeTop.set(`${Math.max(0, header?.getBoundingClientRect().bottom ?? 0)}px`);
    this.chromeBottom.set(
      `${Math.max(0, footer ? window.innerHeight - footer.getBoundingClientRect().top : 0)}px`
    );
    this.isMobile.set(window.innerWidth <= 767);
  }

  protected scrollToSection(sectionId: string): void {
    const container = this.body()?.nativeElement;
    const el = container?.querySelector<HTMLElement>(`[data-help-section="${CSS.escape(sectionId)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.focus({ preventScroll: true });
      this.help.targetSectionId.set(null);
    }
  }

  protected onEscape(): void {
    if (this.zoomedImage()) {
      this.zoomedImage.set(null);
    } else if (this.help.isOpen()) {
      this.help.close();
    }
  }
}
